import { useMemo, type ReactNode } from "react";
import { ArrowDown, ArrowUp, Copy as CopyIcon, ListChecks, ShieldAlert } from "lucide-react";
import { toast } from "@fox1ck-org/ui/toast";
import {
  ActivityTimeline,
  Badge,
  Button,
  CopyableId,
  DescriptionItem,
  DescriptionList,
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  Skeleton,
  type TimelineEvent,
} from "@fox1ck-org/ui";
import type { Click, ClickJourneyStep, ClickTrace, SignalContribution } from "@gen/tracker/v1/click_pb";
import { ConversionStatus, type Conversion } from "@gen/tracker/v1/conversion_pb";

import { ConversionStatusBadge } from "@/components/canonical/conversion-status-badge";
import { humanGap } from "@/components/canonical/conversion-detail-drawer";
import { ruleCondition, ruleOfNode } from "@/components/campaign/rules";
import { getLocale, useT } from "@/lib/i18n";
import { formatMoney } from "@/lib/format";
import { formatStamp, parseDecimalString } from "@/lib/utils";
import { asnLabel, trafficClassLabel, trafficClassTone } from "@/lib/traffic-class";
import { useReportTimezone } from "@/lib/services/workspace-service";
import { useClickJourney, useGetClick, useGetClickTrace } from "@/lib/services/click-service";
import { conversionStatusText, useSearchConversions } from "@/lib/services/conversion-service";
import { useLandersByIds } from "@/lib/services/lander-service";
import { useOffersByIds } from "@/lib/services/offer-service";
import { useGetCampaign, useRenderAdLink } from "@/lib/services/campaign-service";
import { CLICK_LABEL, useLabelClick } from "@/lib/services/fraud-service";
import { WriteOnly } from "@/components/auth/write-only";
import { clickGeo } from "@/lib/geo";

export interface ClickDetailDrawerProps {
  /** When set (and open) the drawer fetches via GetClick — row clicks and the ?click= deep link alike. */
  clickId: string | undefined;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** «Конверсии клика» / a conversion of this click: the screen opens it. */
  onOpenConversion?: (c: Conversion) => void;
  /** ↑ ↓ in the footer: the row above and below in the screen's table. */
  onPrev?: () => void;
  onNext?: () => void;
}

/** bot_score is 0..1 from the edge classifier; the board reads it «из 100». */
export function scoreOf100(score: number): number {
  const pct = score <= 1 ? score * 100 : score;
  return Math.round(Math.max(0, Math.min(100, pct)));
}

/** The signals that moved the verdict most, either way, unavailable ones dropped. Pure. */
export function topSignals(contributions: SignalContribution[], n = 5): SignalContribution[] {
  return [...contributions]
    .filter((c) => !c.unavailable && c.points !== 0)
    .sort((a, b) => Math.abs(b.points) - Math.abs(a.points))
    .slice(0, n);
}

/**
 * Клик (доска CClicks, шторка): one visit beside the table it was opened from
 * — the panel sits next to the rows, it does not cover them. Its path from
 * the link to the offer and how it got there (a rule or the shares), the
 * conversions on it, who it was and why the class says person or bot, and
 * the marks the ad passed. «Скопировать как JSON» hands the whole of it to a
 * partner's support.
 */
export function ClickDetailDrawer({ clickId, open, onOpenChange, onOpenConversion, onPrev, onNext }: ClickDetailDrawerProps) {
  const t = useT();
  const tz = useReportTimezone();
  const click = useGetClick(open ? clickId : undefined);
  const trace = useGetClickTrace(open ? clickId : undefined);
  const journey = useClickJourney(open ? clickId : undefined);
  const k = click.data;
  const conversions = useClickConversions(k);
  const convs = conversions.data?.items ?? [];
  const cls = k ? k.trafficClass || (k.botFlag ? "bot" : "human") : "";

  return (
    <Sheet open={open} onOpenChange={onOpenChange} modal={false}>
      <SheetContent
        side="right"
        hideOverlay
        closeLabel={t("panel.close")}
        className="flex w-135 max-w-[calc(100vw_-_--spacing(12))] flex-col gap-0 p-0 sm:max-w-none"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <SheetHeader className="shrink-0 gap-2 border-b border-border px-5 pb-3 pt-4 text-left">
          <div className="flex min-w-0 items-center gap-2 pr-8">
            <SheetTitle className="shrink-0 text-section-title">{t("click.drawer.title")}</SheetTitle>
            {clickId ? <CopyableId value={clickId} label={t("click.drawer.id")} truncate={false} className="min-w-0 font-mono text-xs text-muted-foreground" /> : null}
          </div>
          <SheetDescription asChild>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              {k ? (
                <>
                  <Badge variant={toneVariant(trafficClassTone(cls))}>{trafficClassLabel(t, cls).toLowerCase()}</Badge>
                  {k.conversionStatus !== ConversionStatus.UNSPECIFIED ? <ConversionStatusBadge status={k.conversionStatus} /> : null}
                  <span>{t("click.meta.at", { at: formatStamp(k.ts, tz, { clock: true }), tz })}</span>
                </>
              ) : null}
            </div>
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-5 py-4">
          {click.isLoading ? (
            <div className="flex flex-col gap-3">
              <Skeleton className="h-24" />
              <Skeleton className="h-24" />
              <Skeleton className="h-40" />
            </div>
          ) : !k ? (
            <p className="text-sm text-muted-foreground">{t("click.notFound")}</p>
          ) : (
            <>
              <PathSection click={k} steps={journey.data ?? []} loading={journey.isLoading} />
              <ConversionsSection click={k} steps={journey.data ?? []} loading={journey.isLoading} convs={convs} onOpen={onOpenConversion} />
              <WhoSection click={k} trace={trace.data} />
              <MarksSection click={k} />
            </>
          )}
        </SheetBody>
        <SheetFooter className="shrink-0 flex-row items-center gap-2 border-t border-border px-5 py-3 sm:justify-start">
          {k ? (
            <>
              <Button variant="secondary" size="sm" disabled={!convs[0] || !onOpenConversion} onClick={() => convs[0] && onOpenConversion?.(convs[0])}>
                <ListChecks className="size-4" strokeWidth={1.5} /> {t("click.drawer.conversions")}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() =>
                  void navigator.clipboard
                    .writeText(clickJson(k, trace.data, journey.data ?? [], convs))
                    .then(() => toast.success(t("click.copied")))
                }
              >
                <CopyIcon className="size-4" strokeWidth={1.5} /> {t("click.drawer.json")}
              </Button>
            </>
          ) : null}
          <span className="flex-1" />
          {onPrev || onNext ? (
            <>
              <Button variant="ghost" size="icon-sm" aria-label={t("click.drawer.prev")} disabled={!onPrev} onClick={onPrev}>
                <ArrowUp className="size-4" />
              </Button>
              <Button variant="ghost" size="icon-sm" aria-label={t("click.drawer.next")} disabled={!onNext} onClick={onNext}>
                <ArrowDown className="size-4" />
              </Button>
            </>
          ) : null}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function toneVariant(tone: string | undefined): "ok" | "warn" | "danger" | "info" | "neutral" {
  return tone === "ok" || tone === "warn" || tone === "danger" || tone === "info" ? tone : "neutral";
}

/** The conversions recorded on one click, from its day until today. */
function useClickConversions(k: Click | undefined) {
  const tz = useReportTimezone();
  const from = k?.ts ? k.ts.slice(0, 10) : "";
  const to = new Date().toISOString().slice(0, 10);
  return useSearchConversions({ range: { from, to, timezone: tz }, search: k?.clickId, limit: 20 }, { enabled: !!k?.clickId && !!from });
}

function Heading({ children }: { children: ReactNode }) {
  return <h3 className="text-label text-muted-foreground">{children}</h3>;
}

// --- Путь -----------------------------------------------------------------

/**
 * The path as chips (доска CClicks): the lander shown, the button, the offer,
 * the lead and where it stands — then how the visit got there: by a rule of
 * the campaign (the node it was resolved at is a rule's) or by the shares.
 */
function PathSection({ click, steps, loading }: { click: Click; steps: ClickJourneyStep[]; loading: boolean }) {
  const t = useT();
  const tz = useReportTimezone();
  const campaign = useGetCampaign(click.campaignId || undefined);
  const landers = useLandersByIds(steps.map((s) => s.landerId).concat(click.landerId));
  const offers = useOffersByIds(steps.map((s) => s.offerId).concat(click.offerId));
  const lander = (id: string) => landers.data?.get(id)?.name ?? t("click.path.lander");
  const offer = (id: string) => offers.data?.get(id)?.name ?? t("click.path.offer");

  const chips = useMemo(() => {
    const out: { key: string; label: ReactNode; tone: "info" | "neutral" | "ok" }[] = [];
    const views = steps.filter((s) => s.kind === "view");
    const ctas = steps.filter((s) => s.kind === "cta").length;
    for (const s of views) out.push({ key: `v${s.ts}`, label: t("click.chip.lander", { name: lander(s.landerId) }), tone: "info" });
    if (ctas > 0) out.push({ key: "cta", label: t("click.chip.cta"), tone: "neutral" });
    for (const s of steps.filter((x) => x.kind === "offer")) out.push({ key: `o${s.ts}`, label: t("click.chip.offer", { name: offer(s.offerId) }), tone: "info" });
    const last = [...steps].reverse().find((s) => s.kind === "status" || s.kind === "lead");
    if (last) {
      const word = last.kind === "lead" ? t("click.path.lead") : conversionStatusText(last.status).toLowerCase();
      out.push({ key: "lead", label: t("click.chip.lead", { status: word }), tone: last.status === ConversionStatus.APPROVED ? "ok" : "neutral" });
    }
    return out;
  }, [steps, landers.data, offers.data, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const c = campaign.data;
  const rule = c ? ruleOfNode(c, click.nodeId) : undefined;
  const how =
    c && rule !== undefined
      ? (() => {
          const cond = ruleCondition(t, c.rules[rule]!, tz);
          return t("click.how.rule", { n: rule + 1, cond: `${cond.lead} ${cond.value}`.trim() });
        })()
      : c && c.rules.length > 0
        ? t("click.how.sharesAfter", { n: c.rules.length })
        : t("click.how.shares");

  return (
    <section className="flex flex-col gap-2">
      <Heading>{t("click.path")}</Heading>
      {loading ? (
        <Skeleton className="h-8" />
      ) : chips.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t("click.path.none")}</p>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((ch) => (
            <Badge key={ch.key} variant={ch.tone} size="md">
              {ch.label}
            </Badge>
          ))}
        </div>
      )}
      <p className="text-xs text-muted-foreground">{how}</p>
    </section>
  );
}

// --- Конверсии этого клика --------------------------------------------------------

function ConversionsSection({
  click,
  steps,
  loading,
  convs,
  onOpen,
}: {
  click: Click;
  steps: ClickJourneyStep[];
  loading: boolean;
  convs: Conversion[];
  onOpen?: (c: Conversion) => void;
}) {
  const t = useT();
  const events = useMemo<TimelineEvent[]>(
    () =>
      steps
        .filter((s) => s.kind === "lead" || s.kind === "status")
        .map((s, i) => {
          const usd = s.payoutUsd ? parseDecimalString(s.payoutUsd) : undefined;
          const after = (Date.parse(s.ts) - Date.parse(click.ts)) / 1000;
          const lead = s.kind === "lead";
          return {
            id: `${s.kind}-${i}-${s.ts}`,
            kind: s.kind,
            at: s.ts,
            title: (
              <span>
                <b className="font-semibold">{lead ? t("click.path.lead") : conversionStatusText(s.status)}</b>
                {!lead && s.status === ConversionStatus.APPROVED && usd ? <span className="ml-1.5 font-mono text-xs text-ok">+{formatMoney(usd, { unit: true })} $</span> : null}
              </span>
            ),
            description: lead
              ? [Number.isFinite(after) && after >= 0 ? t("click.conv.after", { gap: humanGap(after, t) }) : "", s.txid ? t("click.conv.txid", { txid: s.txid }) : ""].filter(Boolean).join(" · ")
              : t("click.conv.byPostback"),
            markerClassName: s.status === ConversionStatus.APPROVED ? "bg-ok" : s.status === ConversionStatus.REJECTED ? "bg-destructive" : "bg-primary",
          };
        }),
    [steps, click.ts, t],
  );
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Heading>{t("click.conversions.title")}</Heading>
        <span className="flex-1" />
        {onOpen && convs[0] ? (
          <Button variant="link" size="sm" className="h-auto p-0" onClick={() => onOpen(convs[0]!)}>
            {t("click.conv.open")}
          </Button>
        ) : null}
      </div>
      {loading ? (
        <Skeleton className="h-14" />
      ) : (
        <ActivityTimeline
          events={events}
          groupBy="none"
          locale={getLocale()}
          emptyMessage={t("click.conversions.none")}
          labels={{ ariaLabel: t("click.conversions.title") }}
        />
      )}
    </section>
  );
}

// --- Кто --------------------------------------------------------------------------

/** Why the class says what it says, in the scoring's own words — the top reasons. Pure. */
export function classWhy(trace: ClickTrace | undefined, n = 3): string {
  return topSignals(trace?.contributions ?? [], n)
    .map((s) => s.reason || s.name)
    .join(", ");
}

function WhoSection({ click, trace }: { click: Click; trace: ClickTrace | undefined }) {
  const t = useT();
  const cls = click.trafficClass || (click.botFlag ? "bot" : "human");
  const why = classWhy(trace);
  const net = [click.asn ? asnLabel(click.asn) : "", click.isp || trace?.asnOrg || ""].filter(Boolean).join(" ");
  const label = useLabelClick();
  return (
    <section className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <Heading>{t("click.who")}</Heading>
        <span className="flex-1" />
        {cls === "human" ? (
          <WriteOnly>
            {/* Fraud feedback: teaches the scoring, does not rewrite this click. */}
            <Button
              variant="link"
              size="sm"
              className="h-auto p-0"
              disabled={label.isPending}
              onClick={() =>
                label.mutate(
                  { clickId: click.clickId, label: CLICK_LABEL.BOT },
                  {
                    onSuccess: () => toast.success(t("click.markedBot")),
                    onError: (e) => toast.error(e instanceof Error ? e.message : t("campaign.toast.failed")),
                  },
                )
              }
            >
              {t("click.markBot")}
            </Button>
          </WriteOnly>
        ) : null}
      </div>
      <DescriptionList>
        <DescriptionItem
          label={t("click.who.class")}
          value={
            <span>
              <b className="font-semibold">{trafficClassLabel(t, cls).toLowerCase()}</b>
              <span className="text-muted-foreground"> · {t("click.who.score", { n: scoreOf100(trace?.botScore ?? click.botScore) })}</span>
              {why ? <span className="text-muted-foreground"> — {why}</span> : null}
            </span>
          }
        />
        <DescriptionItem
          label={t("click.who.geo")}
          value={
            clickGeo(click) || click.ip ? (
              <span>
                {clickGeo(click)}
                {click.ip ? <span className="ml-1.5 font-mono text-xs text-muted-foreground">IP {click.ip}</span> : null}
              </span>
            ) : undefined
          }
        />
        <DescriptionItem
          label={t("click.who.isp")}
          value={net ? <span className="font-mono text-sm">{net}{click.connectionType || trace?.asnType ? <span className="font-sans text-muted-foreground"> · {click.connectionType || trace?.asnType}</span> : null}</span> : undefined}
        />
        <DescriptionItem label={t("click.who.device")} value={[click.device, click.os, click.browser].filter(Boolean).join(" · ") || undefined} />
        <DescriptionItem label={t("click.who.language")} value={click.language || undefined} />
      </DescriptionList>
      {click.sourceKind === "FACEBOOK_ADS" && !click.pixelId && !click.fbclid ? (
        <p className="inline-flex items-center gap-1.5 text-xs text-warn">
          <ShieldAlert className="size-3.5" /> {t("click.who.noMatchKeys")}
        </p>
      ) : null}
    </section>
  );
}

// --- Метки из объявления -------------------------------------------------------------

function subOrder(k: string): number {
  const m = /^sub_?(\d+)$/.exec(k);
  return m ? Number(m[1]) : 1000;
}

/**
 * What the ad passed in the link (доска CClicks): fbclid and the subs, each
 * named by what the source fills it with — the campaign's own link template
 * (RenderAdLink), so «sub1 · объявление» is the source's word, not a guess.
 */
function MarksSection({ click }: { click: Click }) {
  const t = useT();
  const link = useRenderAdLink(click.campaignId || undefined);
  const names = new Map((link.data?.link?.params ?? []).map((p) => [p.param, p.label] as const));
  const subs = Object.entries(click.trackingFields)
    .filter(([, v]) => v)
    .sort(([a], [b]) => subOrder(a) - subOrder(b) || a.localeCompare(b));
  if (!click.fbclid && subs.length === 0) return null;
  return (
    <section className="flex flex-col gap-2">
      <Heading>{t("click.marks")}</Heading>
      <DescriptionList>
        <DescriptionItem label="fbclid" value={click.fbclid ? <span className="break-all font-mono text-xs">{click.fbclid}</span> : undefined} />
        {subs.map(([key, v]) => (
          <DescriptionItem key={key} label={names.get(key) ? `${key} · ${names.get(key)}` : key} value={<span className="break-all font-mono text-xs">{v}</span>} />
        ))}
      </DescriptionList>
    </section>
  );
}

/** Everything about the click as JSON, for a partner's support or a chat. Pure. */
export function clickJson(k: Click, trace: ClickTrace | undefined, steps: ClickJourneyStep[], convs: Conversion[]): string {
  return JSON.stringify(
    {
      click_id: k.clickId,
      ts: k.ts,
      campaign_id: k.campaignId,
      class: k.trafficClass || (k.botFlag ? "bot" : "human"),
      bot_score: scoreOf100(trace?.botScore ?? k.botScore),
      reasons: topSignals(trace?.contributions ?? []).map((s) => s.reason || s.name),
      ip: k.ip,
      network: { isp: k.isp, asn: k.asn, type: k.connectionType },
      geo: clickGeo(k),
      device: { device: k.device, os: k.os, browser: k.browser, language: k.language, ua: k.userAgent },
      lander_id: k.landerId,
      offer_id: k.offerId,
      node_id: k.nodeId,
      meta: { fbclid: k.fbclid, fbp: k.fbp, pixel_id: k.pixelId, ad: k.creativeId },
      subs: Object.fromEntries(Object.entries(k.trackingFields).filter(([, v]) => v)),
      path: steps.map((s) => ({ kind: s.kind, ts: s.ts, lander_id: s.landerId || undefined, offer_id: s.offerId || undefined, status: s.kind === "status" ? conversionStatusText(s.status) : undefined })),
      conversions: convs.map((c) => ({ status: conversionStatusText(c.status), goal: c.goalKey, payout: c.payout?.amount, currency: c.payout?.currency, txid: c.txid, ts: c.ts })),
    },
    null,
    2,
  );
}
